/**
 * useEditMode - Document Editing State Hook
 * 
 * Extracts edit mode management from WorkspacePage.tsx:
 * - Enable/disable edit mode
 * - Auto-switch AI mode when editing
 * - Handle open and edit for recent documents
 * - Document-Conversation pairing with lock acquisition/release
 */

import { useCallback, useState } from 'react';
import { logger } from '../utils/logger';
import type { AeroDocument } from '../types/document-schema';
import type { DocumentEditLock } from '../contexts/AIContext';
import { useToast } from '@/components/ui';
import { conversationsApi } from '../services/api';

export interface OpenedDocumentForEdit {
  tabId: number;
  documentNumber?: string;
  projectId?: string;
  title: string;
  canEdit?: boolean;
  jsonDocument?: AeroDocument;
  version?: string;
  id?: string;
  status?: string;
  currentRevision?: string;
  type?: string;
  s3VersionId?: string; // S3 version ID for undo functionality
  s3JsonKey?: string;   // S3 path for document save
}

export interface ActiveEditDocumentState {
  tabId: number;
  documentNumber: string;
  projectId: string;
  jsonDocument: AeroDocument;
  version: string;
  s3VersionId?: string; // S3 version ID for undo functionality
  s3JsonKey?: string;   // S3 path for document save
}

interface UseEditModeDeps {
  // From AIContext
  openedDocuments: OpenedDocumentForEdit[];
  setActiveEditDocument: (doc: ActiveEditDocumentState | null) => void;
  clearActiveEditDocument: () => void;
  addOpenedDocument: (doc: OpenedDocumentForEdit) => void;
  setActiveTabId: (id: number | null) => void;
  
  // Input control
  setInput: (value: string) => void;
  closeAllAutocomplete: () => void;
  
  // Document lock management (legacy - kept for compatibility)
  activeConversationId: string | null;
  activeConversationTitle: string;
  visibleTabIds: string[]; // For checking if a lock is stale
  acquireDocumentLock: (documentId: string, conversationId: string, conversationTitle: string) => Promise<DocumentEditLock | null>;
  releaseDocumentLock: (documentId: string) => void;
  getDocumentLock: (documentId: string) => DocumentEditLock | undefined;
  transferDocumentLock: (documentId: string, toConversationId: string, toConversationTitle: string) => boolean;
  
  // Link-based architecture: auto-create linked conversation on edit
  createConversation: (title?: string) => Promise<string>;
  switchConversation: (id: string, addToVisibleTabs?: boolean) => Promise<void>;
  linkArtifact: (artifactType: 'document' | 'ssa' | 'diagram' | 'plan', artifactId: string, artifactName: string, projectId: string, conversationId?: string) => Promise<void>;
  // Note: unlinkArtifact not needed here - backend link persists for reuse
  // User breaks link by closing conversation tab (X) in the prompt panel
}

/** State for pending transfer dialog */
export interface PendingTransferState {
  existingLock: DocumentEditLock;
  targetConversationId: string;
  targetConversationTitle: string;
  tabId: number;
  jsonDocument: AeroDocument;
  version: string;
}

interface UseEditModeReturn {
  /** Enable edit mode for a document, auto-linking to AI conversation */
  enableEdit: (tabId: number, jsonDocument: AeroDocument, version: string) => Promise<void>;
  /** Disable edit mode and release the document lock (link persists in backend) */
  disableEdit: (documentNumber?: string) => void;
  openAndEdit: (doc: {
    id: string;
    documentNumber?: string;
    projectId?: string;
    title: string;
    type: string;
    canEdit?: boolean;
    status?: string;
    currentRevision?: string;
  }) => void;
  /** Error message if document is locked by another conversation */
  lockError: string | null;
  /** Clear the lock error */
  clearLockError: () => void;
  /** Pending transfer dialog state (shown when document is locked by another conversation) */
  pendingTransfer: PendingTransferState | null;
  /** Confirm the pending transfer */
  confirmTransfer: () => Promise<void>;
  /** Cancel the pending transfer */
  cancelTransfer: () => void;
}

export function useEditMode(deps: UseEditModeDeps): UseEditModeReturn {
  const {
    openedDocuments,
    setActiveEditDocument,
    clearActiveEditDocument,
    addOpenedDocument,
    setActiveTabId,
    setInput,
    closeAllAutocomplete,
    activeConversationId: _activeConversationId,
    activeConversationTitle: _activeConversationTitle,
    visibleTabIds: _visibleTabIds,
    acquireDocumentLock: _acquireDocumentLock,
    releaseDocumentLock,
    getDocumentLock: _getDocumentLock,
    transferDocumentLock,
    createConversation,
    switchConversation,
    linkArtifact,
  } = deps;
  
  const { warning } = useToast();
  
  // Lock acquisition error state
  const [lockError, setLockError] = useState<string | null>(null);
  
  // Pending transfer dialog state
  const [pendingTransfer, setPendingTransfer] = useState<PendingTransferState | null>(null);
  
  const clearLockError = useCallback(() => {
    setLockError(null);
  }, []);
  
  const cancelTransfer = useCallback(() => {
    setPendingTransfer(null);
    setLockError(null);
  }, []);

  /**
   * Enable edit mode for a document tab
   * Queries backend for existing linked conversation, or creates a new one
   * The link persists in the database - robust across hard reloads
   */
  const enableEdit = useCallback(async (tabId: number, jsonDocument: AeroDocument, version: string) => {
    logger.debug('[useEditMode] enableEdit called:', { tabId });
    
    const doc = openedDocuments.find(d => d.tabId === tabId);
    if (!doc) {
      logger.error('[useEditMode] Document not found for tab', tabId);
      return;
    }
    
    logger.debug('[useEditMode] Found doc:', { documentNumber: doc.documentNumber, canEdit: doc.canEdit });
    
    if (!doc.canEdit) {
      warning('Permission Denied', 'You do not have permission to edit this document.');
      return;
    }
    
    if (!doc.documentNumber || !doc.projectId) {
      logger.error('[useEditMode] Document number and projectId are required for edit mode');
      return;
    }
    
    logger.debug('[useEditMode] Activating edit mode for tab', tabId, doc.title);
    
    // Set active edit document first
    setActiveEditDocument({
      tabId,
      documentNumber: doc.documentNumber,
      projectId: doc.projectId,
      jsonDocument,
      version,
      s3VersionId: doc.s3VersionId,
      s3JsonKey: doc.s3JsonKey,
    });
    
    // Query backend for existing conversation linked to this document
    // This is robust across hard reloads - link is stored in database
    try {
      // Determine artifact type: 'plan' for AI plans, 'document' for regular docs
      const isAIPlan = jsonDocument?.metadata?.documentType === 'ai_plan';
      const artifactType = isAIPlan ? 'plan' : 'document';
      
      logger.debug('[useEditMode] Document type detection:', { 
        documentType: jsonDocument?.metadata?.documentType,
        isAIPlan,
        artifactType 
      });
      
      const result = await conversationsApi.getByArtifact(artifactType, doc.documentNumber);
      const existingConversations = result?.conversations || [];
      
      logger.debug('[useEditMode] Backend query result:', { 
        found: existingConversations.length,
        documentNumber: doc.documentNumber 
      });
      
      if (existingConversations.length > 0) {
        // Reuse the most recent linked conversation
        const mostRecent = existingConversations[0];
        logger.debug('[useEditMode] Found existing linked conversation in backend:', mostRecent.conversationId);
        // Pass true to add to visible tabs (conversation may have been hidden)
        await switchConversation(mostRecent.conversationId, true);
      } else {
        // Create new linked conversation
        // For plans, use documentNumber as the primary identifier (matches tab display)
        // For regular docs, use title (which is typically more descriptive)
        const docTitle = isAIPlan 
          ? doc.documentNumber || doc.title || 'Plan'
          : doc.title || doc.documentNumber;
        const sessionLabel = isAIPlan ? 'Plan Session' : 'AI Session';
        const newConversationId = await createConversation(`${docTitle} - ${sessionLabel}`);
        
        if (newConversationId) {
          await linkArtifact(artifactType, doc.documentNumber, docTitle, doc.projectId, newConversationId);
          logger.debug('[useEditMode] Created new linked conversation:', newConversationId, 'artifactType:', artifactType);
          
          // CRITICAL: Switch to the new conversation to properly load its state from backend
          // This ensures conversation links and other state are properly synchronized
          await switchConversation(newConversationId, true);
        }
      }
    } catch (error) {
      logger.error('[useEditMode] Failed to find/create linked conversation:', error);
      // Edit mode still works, just without AI assistance
    }
  }, [openedDocuments, setActiveEditDocument, switchConversation, createConversation, linkArtifact, warning]);

  /**
   * Confirm the pending transfer and enable edit mode
   */
  const confirmTransfer = useCallback(async () => {
    if (!pendingTransfer) {
      logger.warn('[useEditMode] No pending transfer to confirm');
      return;
    }
    
    const { existingLock, targetConversationId, targetConversationTitle, tabId, jsonDocument, version } = pendingTransfer;
    
    logger.debug('[useEditMode] Confirming transfer from', existingLock.lockedByConversationTitle, 'to', targetConversationTitle);
    
    // Transfer the lock
    const success = transferDocumentLock(existingLock.documentId, targetConversationId, targetConversationTitle);
    if (!success) {
      setLockError('Failed to transfer document lock');
      setPendingTransfer(null);
      return;
    }
    
    // Find the document again (in case openedDocuments changed)
    const doc = openedDocuments.find(d => d.tabId === tabId);
    if (!doc) {
      logger.error('[useEditMode] Document not found after transfer');
      setPendingTransfer(null);
      return;
    }
    
    // Enable edit mode
    logger.debug('[useEditMode] Transfer complete, enabling edit mode');
    setPendingTransfer(null);
    setLockError(null);
    
    setActiveEditDocument({
      tabId,
      documentNumber: doc.documentNumber!,
      projectId: doc.projectId!,
      jsonDocument,
      version,
      s3VersionId: doc.s3VersionId,
      s3JsonKey: doc.s3JsonKey,
    });
    
    // Link-based architecture: Do NOT auto-switch to Agent mode
    // User must use "Link AI" button to explicitly link document
  }, [pendingTransfer, transferDocumentLock, openedDocuments, setActiveEditDocument]);

  /**
   * Disable edit mode and release the document lock
   * Link=Edit model: Backend link persists for conversation reuse
   * Only closing the conversation tab (X) breaks the backend link
   */
  const disableEdit = useCallback((documentNumber?: string) => {
    logger.debug('[useEditMode] Disabling edit mode');
    
    // Release the document lock if we have a document number
    if (documentNumber) {
      logger.debug('[useEditMode] Releasing document lock for:', documentNumber);
      releaseDocumentLock(documentNumber);
    }
    
    // Link=Edit: Clear frontend edit state only
    // Backend link persists so re-editing reuses the same conversation
    // User can break link by closing conversation tab (X)
    clearActiveEditDocument();
    setLockError(null);
    logger.debug('[useEditMode] Edit mode disabled (backend link persists for reuse)');
  }, [clearActiveEditDocument, releaseDocumentLock]);

  /**
   * Open a document and immediately enable edit mode
   */
  const openAndEdit = useCallback((doc: {
    id: string;
    documentNumber?: string;
    projectId?: string;
    title: string;
    type: string;
    canEdit?: boolean;
    status?: string;
    currentRevision?: string;
  }) => {
    logger.debug('[useEditMode] @edit: Opening and editing recent document:', doc.title, 'Status:', doc.status);
    
    // Save to recent documents
    try {
      const saved = localStorage.getItem('aeroRecentDocuments');
      const recent = saved ? JSON.parse(saved) : [];
      const filtered = recent.filter((r: { id?: string }) => r.id !== doc.id);
      const updated = [doc, ...filtered].slice(0, 10);
      localStorage.setItem('aeroRecentDocuments', JSON.stringify(updated));
    } catch (error) {
      logger.error('Failed to save recent document:', error);
    }
    
    // Determine if document is editable based on status (AS9100D compliance)
    const isEditable = doc.status === 'Draft' || doc.status === 'In Review';
    
    // Add document to opened documents
    addOpenedDocument({
      id: doc.id,
      documentNumber: doc.documentNumber,
      projectId: doc.projectId,
      title: doc.title,
      type: doc.type as 'project' | 'company' | 'standard' | 'prior-project',
      canEdit: isEditable,
      status: doc.status,
      currentRevision: doc.currentRevision,
    });
    
    // Wait for document to be added, then enable edit mode (if editable)
    if (isEditable) {
      setTimeout(() => {
        // Find the newly added document
        const newDoc = openedDocuments.find(d => d.id === doc.id);
        if (newDoc) {
          setActiveTabId(newDoc.tabId);
          // Wait a bit more for document content to load, then click edit button
          setTimeout(() => {
            const enableButton = document.querySelector('[data-edit-button]') as HTMLButtonElement;
            if (enableButton) {
              enableButton.click();
              logger.debug('[useEditMode] @edit: Clicked Enable Editing button for new document');
            } else {
              logger.warn('[useEditMode] @edit: Edit button not found, document may still be loading');
              // Retry after another delay
              setTimeout(() => {
                const retryButton = document.querySelector('[data-edit-button]') as HTMLButtonElement;
                if (retryButton) {
                  retryButton.click();
                  logger.debug('[useEditMode] @edit: Clicked Enable Editing button (retry)');
                }
              }, 500);
            }
          }, 800);
        } else {
          // Document was just added, get the max tab ID
          const maxTabId = openedDocuments.length > 0 ? Math.max(...openedDocuments.map(d => d.tabId)) : 0;
          setActiveTabId(maxTabId + 1);
          // Wait for document to load
          setTimeout(() => {
            const enableButton = document.querySelector('[data-edit-button]') as HTMLButtonElement;
            if (enableButton) {
              enableButton.click();
              logger.debug('[useEditMode] @edit: Clicked Enable Editing button for new document');
            }
          }, 800);
        }
      }, 100);
    } else {
      logger.debug('[useEditMode] @edit: Document is not editable (status:', doc.status, '). Open to view and use New Revision to create draft.');
      // Still set as active tab so user can see it
      setTimeout(() => {
        const newDoc = openedDocuments.find(d => d.id === doc.id);
        if (newDoc) {
          setActiveTabId(newDoc.tabId);
        } else {
          const maxTabId = openedDocuments.length > 0 ? Math.max(...openedDocuments.map(d => d.tabId)) : 0;
          setActiveTabId(maxTabId + 1);
        }
      }, 100);
    }
    
    // Clear input and autocomplete
    setInput('');
    closeAllAutocomplete();
  }, [openedDocuments, addOpenedDocument, setActiveTabId, setInput, closeAllAutocomplete]);

  return {
    enableEdit,
    disableEdit,
    openAndEdit,
    lockError,
    clearLockError,
    pendingTransfer,
    confirmTransfer,
    cancelTransfer,
  };
}
